import React, { useEffect, useState } from 'react';
import Card from '../../../components/Cards/Card';
import { useTranslation } from 'react-i18next';
import BootstrapTable from 'react-bootstrap-table-next';
import { Col, Row } from 'react-bootstrap';
import moment from 'moment';
import { Add, Check, Edit, More, Remove } from '../../../components/Icons/Icons';
import { Tooltip } from '../../../components/Utils/Tooltips';
import { useHistory } from "react-router-dom"
import paginationFactory from 'react-bootstrap-table2-paginator';

import useMoves from '../Hooks/useMoves';
import { PaginationRequest, UniqueId } from '../../../api/Common/Util';
import Loader from '../../../components/Common/Loader';
import { MoveStatus, MoveTypes } from '../../../api/Inventory/Entities/InventoryMovementEntities';
import { EnumValue } from '../../../api/Freezer/Entities/Utils';
import useConfirmationModal from '../../../components/Modals/ConfirmationModal/useConfirmationModal';
import InventoryOperationService from '../../../api/Inventory/InventoryOperationService';
import { ErrorMessage, Success } from '../../../components/Notifications/Notifications';
import { useSameUser } from '../Hooks/useSameUser';
import SearchHeader from '../../../components/Common/SearchHeader';
import { WithAuth } from '../../../components/Authorization/WithAuth';
import { useAuthorization } from '../../../components/Authorization/useAuthorization';

export default function InventoryOperationsPage() {
    const { t } = useTranslation();
    const [isSameUser, sessionUser] = useSameUser();
    const [isAuthenticated, user, isAuthorized] = useAuthorization();
    const [canEdit, setCanEdit] = useState(false)
    const [pagedMoves, isLoading, navigation] = useMoves({
        ...new PaginationRequest({
            pageSize: 10,
            pageNumber: 1,
            sortColumn: 'createat',
            sortOrder: 'desc',
        }), includedTypes: 'add_stock,adjust_stock',
    })
    const history = useHistory();
    const [Modal, modalActions] = useConfirmationModal();

    useEffect(() => {
        if (isAuthorized(["GeneralInventory.Edit", "ProtocolInventory.Edit", "LaboratoryKitInventory.Edit"])) {
            setCanEdit(true)
        } else {
            setCanEdit(false)
        }
    }, [isAuthenticated])


    function handleAddStock() {
        history.push('/admin/inventory/addstock/0')
    }
    function handleProtocolAddStock() {
        history.push('/admin/inventory/protocol/addstock/0')
    }
    function handleAdjustStock() {
        history.push('/admin/inventory/adjuststock/0')
    }
    function handleProtocolAdjustStock() {
        history.push('/admin/inventory/protocol/adjuststock/0')
    }
    function handleShowMoveDetail(moveId) {
        history.push('/admin/inventory/moves/' + moveId)
    }
    function handleLaboratoryKitAddStock() {
        history.push('/admin/inventory/laboratorykit/addstock/0')
    }
    function handleLaboratoryKitAdjustStock() {
        history.push('/admin/inventory/laboratorykit/adjuststock/0')
    }

    function handleTableChange(type, { page, sizePerPage, sortField, sortOrder }) {
        navigation.ToPage({
            page
        })
    }

    function handleCancelMovement(move) {
        modalActions.Show({
            title: t("inventory.commons.cancelOperationTitle"),
            textContent: t('inventory.commons.confirmCancel'),
            buttonText: t('buttons.delete'),
            buttonType: 'danger',
            requireComment: true,
            onConfirmation: async ({ comment }) => {
                const result = await InventoryOperationService.CancelPending(move.id, sessionUser.sub, comment)
                if (result.Succeeded)
                    Success(t('commons.deleteSuccess'))
                else
                    ErrorMessage(t('commons.deleteError'))

                navigation.Reload()
            }
        })
    }

    function handleResumeMove(move) {

        switch (move.assignmentType) {
            case "protocol":
                if (MoveTypes.IsEquivalent(move.moveType, MoveTypes.AddStock))
                    history.push(`/admin/inventory/protocol/addstock/${move.id}`)
                if (MoveTypes.IsEquivalent(move.moveType, MoveTypes.AdjustStock))
                    history.push(`/admin/inventory/protocol/adjuststock/${move.id}`)
                break;
            case "general":
                if (MoveTypes.IsEquivalent(move.moveType, MoveTypes.AddStock))
                    history.push(`/admin/inventory/addstock/${move.id}`)
                if (MoveTypes.IsEquivalent(move.moveType, MoveTypes.AdjustStock))
                    history.push(`/admin/inventory/adjuststock/${move.id}`)
                break;
            case "protocollaboratorykit":
                if (MoveTypes.IsEquivalent(move.moveType, MoveTypes.AddStock))
                    history.push(`/admin/inventory/laboratorykit/addstock/${move.id}`)
                if (MoveTypes.IsEquivalent(move.moveType, MoveTypes.AdjustStock))
                    history.push(`/admin/inventory/laboratorykit/adjuststock/${move.id}`)
                break;
            default:
                throw new Error("Invalid Assignment Type")
        }
    }

    const columns =
        [
            {
                dataField: 'user.fullName', text: t('commons.requester'), headerStyle: { width: '15rem' }
            },
            {
                dataField: 'moveType', text: t('commons.type'), headerStyle: { width: '7rem' }
                , formatter: (cell, row) => t(`inventory.movements.moveType.${cell}`)
            },
            {
                text: t('commons.date'), headerStyle: { width: '6rem' }
                , formatter: function (_cell, move, rowIndex) {
                    return moment(move.createAt).format("DD/MM/YYYY HH:mm");
                }
            },
            {
                dataField: 'status', text: t('commons.state'), headerStyle: { width: '7rem' }
                , formatter: (cell, row) => t(`inventory.movements.moveState.${cell}`)
            },
            {
                dataField: 'assignmentType', text: t('inventory.movements.commons.destination'), headerStyle: { width: '7rem' }
                , formatter: (cell, row) => `${t('inventory.movements.assignmentType.' + cell)}`
            },
            {
                text: t('inventory.movements.commons.assignment'), headerStyle: { width: '7rem' }
                , formatter: (cell, row) => row.assignName
            },
            {
                dataField: 'view',
                text: "",
                isDummyField: true,
                headerClasses: 'header-actions',
                classes: 'row-actions',
                formatter: function (_cell, row, rowIndex) {
                    const isSame = isSameUser(row.user);
                    return (
                        <div key={rowIndex}>
                            <button className="btn-table-actions" onClick={() => { handleShowMoveDetail(row.id) }}>
                                <Tooltip tooltip={t('commons.viewDetails')} id={"tooltipedit-" + row.Id}>
                                    <span aria-hidden="true"><More /></span>
                                </Tooltip>
                            </button>

                            <WithAuth requiredPermission={["GeneralInventory.Edit", "ProtocolInventory.Edit", "LaboratoryKitInventory.Edit"]}>
                                {isSame && row.status == "pending" && <button className="btn-table-actions" onClick={() => { handleCancelMovement(row) }}>
                                    <Tooltip tooltip={t('inventory.commons.cancelOperationTitle')} id={"tooltipedit-" + row.Id}>
                                        <span aria-hidden="true"><Remove /></span>
                                    </Tooltip>
                                </button>}
                                {isSame && row.status == "pending" && <button className="btn-table-actions" onClick={() => { handleResumeMove(row) }}>
                                    <Tooltip tooltip={t('commons.resume')} id={"tooltipedit-" + row.Id}>
                                        <span aria-hidden="true"><Edit /></span>
                                    </Tooltip>
                                </button>}
                            </WithAuth>
                        </div>
                    )
                }
            }
        ]

    const All = new EnumValue(0, 'all')
    const inputs = [
        { type: 'select', title: t('commons.type'), name: 'type', options: [All, MoveTypes.AddStock, MoveTypes.AdjustStock], optionName: (op) => t(`inventory.movements.moveType.${op.Name?.toLowerCase()}`), onChange: (e, v) => { }, disabled: false, value: null, col: { sm: 12, md: 2, lg: 2 } },
        { type: 'select', title: t('commons.status'), name: 'status', options: [All, MoveStatus.Completed, MoveStatus.Pending, MoveStatus.Canceled], optionName: (op) => t(`inventory.movements.moveState.${op.Name?.toLowerCase()}`), onChange: (e, v) => { }, disabled: false, value: null, col: { sm: 12, md: 2, lg: 2 } },
    ]
    const handleSearch = async (filter) => {
        await navigation.Search({
            includedTypes: filter.type?.Name != All.Name ? filter.type?.Name : 'add_stock,adjust_stock',
            status: filter.status?.Name != All.Name ? filter.status?.Name : null,
        })
    }

    return (
        <>
            <Card
                content={
                    <Row>
                        {canEdit && <Col sm={12} md={4} lg={2}>
                            <Card className="card-actions"
                                content={<>
                                    <WithAuth requiredPermission={["GeneralInventory.Edit"]}>
                                        <button className={`btn btn-info btn-fill btn-full`} onClick={handleAddStock}>
                                            <Add /> {t('inventory.buttons.addStock')}
                                        </button>
                                    </WithAuth>
                                    <WithAuth requiredPermission={["ProtocolInventory.Edit"]}>
                                        <button className={`btn btn-info btn-fill btn-full`} onClick={handleProtocolAddStock}>
                                            <Add /> {t('inventory.buttons.addStockToProtocol')}
                                        </button>
                                    </WithAuth>
                                    <WithAuth requiredPermission={["LaboratoryKitInventory.Edit"]}>
                                        <button className={`btn btn-info btn-fill btn-full`} onClick={handleLaboratoryKitAddStock}>
                                            <Add /> {t('inventory.buttons.addStockToLaboratoryKit')}
                                        </button>
                                    </WithAuth>
                                    <WithAuth requiredPermission={["GeneralInventory.Edit"]}>
                                        <button className={`btn btn-warning btn-fill btn-full`} onClick={handleAdjustStock}>
                                            <Check /> {t('inventory.buttons.adjustStock')}
                                        </button>
                                    </WithAuth>
                                    <WithAuth requiredPermission={["ProtocolInventory.Edit"]}>
                                        <button className={`btn btn-warning btn-fill btn-full`} onClick={handleProtocolAdjustStock}>
                                            <Check /> {t('inventory.buttons.adjustStockToProtocol')}
                                        </button>
                                    </WithAuth>
                                    <WithAuth requiredPermission={["LaboratoryKitInventory.Edit"]}>
                                        <button className={`btn btn-warning btn-fill btn-full`} onClick={handleLaboratoryKitAdjustStock}>
                                            <Check /> {t('inventory.buttons.adjustStockToLaboratoryKit')}
                                        </button>
                                    </WithAuth>
                                </>}
                            />
                        </Col>}
                        <Col sm={12} md={canEdit ? 8 : 12} lg={canEdit ? 10 : 12}>
                            <div>
                                <div style={{ width: '100%' }}>
                                    <SearchHeader inputs={inputs} handleSearch={handleSearch} init={{ type: All, status: All }} buttonTitle={t('buttons.apply')} />
                                </div>
                                <div>
                                    <Loader isLoading={isLoading}>
                                        <BootstrapTable
                                            bordered={false}
                                            hover
                                            remote
                                            condensed
                                            keyField='id'
                                            classes="table-spaced table-spaced-small"
                                            wrapperClasses="table-responsive"

                                            pagination={paginationFactory({
                                                page: pagedMoves.PageNumber,
                                                sizePerPage: pagedMoves.PageSize,
                                                totalSize: pagedMoves.TotalCount,
                                                hideSizePerPage: true
                                            })}

                                            onTableChange={handleTableChange}

                                            data={pagedMoves.Values}
                                            columns={columns}
                                            loading={isLoading}
                                            noDataIndication={() => <>{t("commons.noData")}</>}
                                        />
                                    </Loader>
                                </div>
                            </div>
                        </Col>
                    </Row>
                }
            />
            <Modal />
        </>
    )
}
